import {
    BehaviorSubject,
    Observable,
    catchError,
    finalize,
    map,
    of,
    tap,
} from 'rxjs';
import { Injectable, inject } from '@angular/core';
import { Assistant } from '../models/assistant.type';
import { HttpErrorResponse } from '@angular/common/http';
import { Environment } from '../../../environments/environment';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Page } from 'conversai-components';
import { LoggerService } from './logger.service';
import { QueryableService } from './crud.service';

/**
 * Cache global de assistentes do usuário, sobre o endpoint de leitura
 * `/assistants`. Diferente do `AccountManagerAssistantService` (CRUD do
 * gerenciador sobre `/manager/assistants`), aqui só lemos: mantém uma lista
 * em `BehaviorSubject` e um cache por id, alimentando a feature de conversa
 * e o init chain da aplicação. Estende {@link QueryableService} para reusar
 * `findById` e os construtores de consulta da base.
 */
@Injectable({
    providedIn: 'root',
})
export class AssistantService extends QueryableService<Assistant> {
    private readonly logger = inject(LoggerService);

    private readonly assistantsSubject = new BehaviorSubject<Assistant[]>([]);
    private readonly assistants$ = this.assistantsSubject.asObservable();
    private readonly isLoadingSubject = new BehaviorSubject<boolean>(true);
    readonly isLoading$ = this.isLoadingSubject.asObservable();
    private readonly cache: Map<string, Assistant> = new Map();

    constructor() {
        super(Environment.apiUrl, 'assistants');
    }

    /**
     * Solicita a atualização da lista de assistentes.
     *
     * Não retorna essa lista, apenas atualiza o estado interno
     * e notifica os observadores sobre esta mudança.
     */
    refresh(): void {
        this.httpClient
            .get<Page<Assistant>>(`${this.endpoint}?per_page=10000`)
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                // Sem conta selecionada ("Fora das Contas"), o backend retorna 403.
                // Nesse contexto a lista de conta é vazia por definição — não é erro.
                catchError((error: HttpErrorResponse) => {
                    if (error.status === 403 || error.status === 401) {
                        return of({
                            data: [],
                            total: 0,
                            currentPage: 0,
                            pageSize: 0,
                        } as unknown as Page<Assistant>);
                    }
                    throw error;
                }),
                finalize(() => this.isLoadingSubject.next(false)),
            )
            .subscribe({
                next: (assistants: Page<Assistant>) => {
                    this.assistantsSubject.next(assistants.data as Assistant[]);
                    this.cache.clear();
                    assistants.data.forEach((assistant) => {
                        this.cache.set(assistant.id!, assistant);
                    });
                },
                error: (error) => {
                    this.logger.error(
                        '[AssistantService] erro ao atualizar assistentes',
                        error,
                    );
                    this.assistantsSubject.next([]);
                    this.isLoadingSubject.next(false);
                },
            });
    }

    /**
     * Obtém a stream que notifica quando a lista de assistentes é atualizada.
     * A stream emite a nova lista de assistentes.
     *
     * @param refresh Se o refresh deve ser forçado.
     * @returns Stream que emite os assistentes.
     */
    onAssistantsRetrieved(refresh: boolean = true): Observable<Assistant[]> {
        if (refresh) {
            setTimeout(() => {
                this.refresh();
            }, 1000);
        }

        return this.assistants$;
    }

    /**
     * Carrega assistentes e retorna a lista como Observable completável.
     * Usado no init chain (app.config.ts) e no retry do NoAssistantComponent.
     * Não usa timeout — retorna direto sem espera.
     *
     * @returns Observable que emite a lista de assistentes carregada.
     */
    refreshAndGet(): Observable<Assistant[]> {
        this.isLoadingSubject.next(true);
        return this.httpClient
            .get<Page<Assistant>>(`${this.endpoint}?per_page=10000`)
            .pipe(
                map((result) => result.data as Assistant[]),
                tap((list) => {
                    this.cache.clear();
                    list.forEach((assistant) => {
                        this.cache.set(assistant.id!, assistant);
                    });
                    this.assistantsSubject.next(list);
                }),
                catchError((error: HttpErrorResponse) => {
                    if (error.status !== 403 && error.status !== 401) {
                        this.logger.error(
                            '[AssistantService] erro em refreshAndGet',
                            error,
                        );
                    }
                    this.assistantsSubject.next([]);
                    return of([]);
                }),
                finalize(() => this.isLoadingSubject.next(false)),
            );
    }
}
